import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Ticket, Loader2, Trash2, Plus } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface Voucher {
  id: string;
  codigo: string;
  plano: 'expert' | 'max_pro';
  data_vencimento: string;
  empresa_id: string | null;
  usado_em: string | null;
  created_at: string;
}

export function VouchersSection() {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [codigo, setCodigo] = useState('');
  const [plano, setPlano] = useState<'expert' | 'max_pro'>('expert');
  const [dataVencimento, setDataVencimento] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchVouchers = async () => {
    const { data, error } = await supabase
      .from('vouchers' as any)
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      console.error('Erro ao carregar vouchers:', error);
    } else {
      setVouchers((data as any) || []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchVouchers(); }, []);

  const handleCreate = async () => {
    if (!codigo.trim() || !dataVencimento) {
      toast({ title: 'Preencha código e data de vencimento', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      const { error } = await supabase.from('vouchers' as any).insert({
        codigo: codigo.trim().toUpperCase(),
        plano,
        data_vencimento: new Date(dataVencimento + 'T23:59:59').toISOString(),
      });
      if (error) throw error;
      toast({ title: 'Voucher criado', description: codigo.toUpperCase() });
      setCodigo(''); setDataVencimento(''); setPlano('expert');
      await fetchVouchers();
    } catch (e: any) {
      toast({
        title: 'Erro ao criar voucher',
        description: e?.code === '23505' ? 'Este código já existe.' : (e?.message || 'Tente novamente'),
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('vouchers' as any).delete().eq('id', id);
    if (error) {
      toast({ title: 'Erro ao excluir', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Voucher excluído' });
      await fetchVouchers();
    }
  };

  const disponiveis = vouchers.filter(v => !v.empresa_id);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Ticket className="w-5 h-5" />
          Vouchers
        </CardTitle>
        <CardDescription>
          Crie vouchers para liberar planos pagos. Cada voucher é válido para apenas uma empresa.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Form */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end p-4 border border-border rounded-lg bg-muted/30">
          <div className="space-y-1">
            <Label htmlFor="v-codigo">Código</Label>
            <Input
              id="v-codigo"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              placeholder="EX: BIANCHIM2026"
              className="uppercase"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="v-plano">Plano</Label>
            <Select value={plano} onValueChange={(v) => setPlano(v as any)}>
              <SelectTrigger id="v-plano"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="expert">Expert</SelectItem>
                <SelectItem value="max_pro">Max Pro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="v-data">Vencimento</Label>
            <Input
              id="v-data"
              type="date"
              value={dataVencimento}
              onChange={(e) => setDataVencimento(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
            />
          </div>
          <Button onClick={handleCreate} disabled={creating}>
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Criar voucher</>}
          </Button>
        </div>

        {/* Lista de disponíveis */}
        <div>
          <h4 className="text-sm font-semibold mb-2 text-muted-foreground">
            Vouchers disponíveis ({disponiveis.length})
          </h4>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Plano</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {disponiveis.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell className="font-mono font-medium">{v.codigo}</TableCell>
                    <TableCell>{v.plano === 'expert' ? 'Expert' : 'Max Pro'}</TableCell>
                    <TableCell>{new Date(v.data_vencimento).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell>{new Date(v.created_at).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir voucher {v.codigo}?</AlertDialogTitle>
                            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(v.id)}>Excluir</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
                {disponiveis.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                      Nenhum voucher disponível. Crie um acima.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
